import axios from 'axios';
import type {
  LoginData,
  RegisterData,
  User,
  Workspace,
  CreateWorkspaceData
} from '../types';

// API URL from environment variable
// Always uses the full API URL (no proxy)
// Fallback to localhost:5000 if not set
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Log the API URL in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('API URL:', API_URL);
}

// Axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login if:
    // 1. It's a 401 error
    // 2. User is currently authenticated (has a token)
    // 3. Not on login/register/forgot-password pages (to allow those endpoints to handle their own 401s)
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                          error.config?.url?.includes('/auth/register') ||
                          error.config?.url?.includes('/auth/forgot-password') ||
                          error.config?.url?.includes('/auth/reset-password');

    if (error.response?.status === 401 && !isAuthEndpoint && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (data: RegisterData) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (data: LoginData) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get<{ user: User }>('/auth/me');
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  updateProfile: async (data: Partial<User>) => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.put('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },
};

// Workspace API
export const workspaceAPI = {
  getAll: async () => {
    const response = await api.get<{ workspaces: Workspace[] }>('/workspaces');
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<{ workspace: Workspace }>(`/workspaces/${id}`);
    return response.data;
  },

  create: async (data: CreateWorkspaceData) => {
    const response = await api.post<{ workspace: Workspace }>('/workspaces', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Workspace>) => {
    const response = await api.put<{ workspace: Workspace }>(`/workspaces/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/workspaces/${id}`);
    return response.data;
  },
};

export default api;
